import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DramaCard from "@/components/feature/DramaCard";
import EmptyState from "@/components/feature/EmptyState";

const wrap = "mx-auto w-full max-w-[1280px] px-4 md:px-6";

const demoComments = [
  {
    name: "追剧小行星",
    date: "2026-09-20",
    content: "两位主演的化学反应真的绝了，重看了三遍还是会被某些桥段戳中。剧情节奏把控得很好，没有多余的注水。",
  },
  {
    name: "海边的风",
    date: "2026-09-11",
    content: "从简介点进来，一口气看完了。人物塑造很立体，配角也各有故事，推荐给同样喜欢这类题材的朋友。",
  },
  {
    name: "柠檬汽水",
    date: "2026-08-29",
    content: "画面质感和配乐都很用心，情感线铺得很自然，看完之后好几天都缓不过来。期待能有更多同类型的作品。",
  },
];

export default function DetailPage() {
  const { id = "" } = useParams();
  const drama = dramas.find((d) => d.id === id);
  const [toast, setToast] = useState("");
  const { isFavorite, toggle } = useFavorites();

  usePageMeta(
    drama ? `${drama.title} - 详细剧情与角色介绍 - 泰剧腐剧双男主` : "剧集详情 - 泰剧腐剧双男主",
    drama
      ? `${drama.title}（${drama.subtitle}）剧情简介、主演阵容与剧集列表：${drama.shortIntro}`
      : "泰国双男主剧集详情页",
  );

  if (!drama) {
    return (
      <div className={`${wrap} py-20`}>
        <EmptyState
          icon="ri-film-line"
          title="没有找到这部作品"
          description="该剧集可能已下架或链接有误，去首页看看其他精彩推荐吧。"
        />
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const leads = drama.subtitle.split("×").map((s) => s.trim());
  const episodes = Array.from({ length: drama.episodeCount }, (_, i) => i + 1);
  const related = dramas
    .filter((d) => d.id !== drama.id && (d.category === drama.category || d.tags.some((t) => drama.tags.includes(t))))
    .slice(0, 4);
  const favorited = isFavorite(drama.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: drama.title, text: drama.shortIntro, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("分享未能完成，可手动复制地址栏链接");
    }
  };

  const infoItems = [
    { label: "年份", value: `${drama.year} 年`, icon: "ri-calendar-line" },
    { label: "地区", value: drama.region, icon: "ri-earth-line" },
    { label: "类型", value: drama.category, icon: "ri-movie-2-line" },
    { label: "时长", value: drama.duration, icon: "ri-time-line" },
    { label: "导演", value: drama.director, icon: "ri-clapperboard-line" },
    { label: "集数", value: `全 ${drama.episodeCount} 集`, icon: "ri-list-check" },
  ];

  return (
    <div className={wrap}>
      <div className="pt-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: drama.category, to: `/category/${drama.category}` },
            { label: drama.title },
          ]}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-8">
        <div>
          <div className="relative mx-auto aspect-[2/3] w-full max-w-[280px] overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:max-w-none">
            <img
              src={drama.cover}
              alt={`${drama.title} 剧照`}
              title={`${drama.title} - 泰国双男主剧集`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="mx-auto mt-4 flex max-w-[280px] gap-3 lg:max-w-none">
            <button
              type="button"
              onClick={() => {
                toggle(drama.id);
                showToast(favorited ? "已取消收藏" : "已加入收藏");
              }}
              className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                favorited
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-share-forward-line"></i>分享
            </button>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            {drama.hot && (
              <span className="whitespace-nowrap rounded-full bg-primary-500 px-2.5 py-0.5 text-xs text-background-50">
                热播推荐
              </span>
            )}
            <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-0.5 text-xs text-accent-900">
              <i className="ri-star-fill"></i>
              {drama.rating.toFixed(1)} 分
            </span>
          </div>
          <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            {drama.title}
          </h1>
          <p className="mt-2 font-heading text-base font-bold text-primary-600 md:text-lg">{drama.subtitle}</p>

          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{drama.shortIntro}</p>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {infoItems.map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-100 p-3">
                <p className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className={item.icon}></i>
                  {item.label}
                </p>
                <p className="mt-1 line-clamp-1 text-sm font-medium text-foreground-900">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <div className="flex flex-wrap gap-2">
              {drama.tags.map((t) => (
                <Link
                  key={t}
                  to={`/tags/${t}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer whitespace-nowrap"
                >
                  {t}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {(drama.views / 10000).toFixed(1)} 万人关注
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-chat-3-line"></i>
              {drama.comments.toLocaleString()} 条讨论
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-check-line"></i>
              更新于 {drama.updateDate}
            </span>
          </div>

          <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-4">
            <p className="flex items-start gap-2 text-sm leading-relaxed text-primary-900">
              <i className="ri-information-line mt-0.5 text-base"></i>
              本站为影视内容聚合与推荐平台，不提供在线播放服务。剧集信息仅供参考。
            </p>
          </div>
        </div>
      </div>

      <section className="mt-12">
        <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className="ri-file-text-line text-lg"></i>
          </span>
          剧情简介
        </h2>
        <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{drama.synopsis}</p>
      </section>

      <section className="mt-12 bg-background-100 -mx-4 px-4 py-8 md:-mx-6 md:px-6 md:py-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-group-line text-lg"></i>
          </span>
          主演与人物关系
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {leads.map((lead, i) => (
            <div key={lead} className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-lg font-bold text-secondary-800">
                {i === 0 ? "男一" : "男二"}
              </span>
              <div>
                <p className="font-heading text-base font-bold text-foreground-950">{lead}</p>
                <p className="mt-1 text-sm text-foreground-500">
                  {i === 0 ? "故事的核心男主，情感线的关键一方" : "与男主命运交织的另一半，共同推动剧情发展"}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {drama.cast.map((c) => (
            <span key={c} className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600 whitespace-nowrap">
              <i className="ri-user-star-line mr-1"></i>
              {c}
            </span>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className="ri-play-list-line text-lg"></i>
          </span>
          剧集列表
        </h2>
        <p className="mt-2 text-sm text-foreground-500">全 {drama.episodeCount} 集 · 已更新至第 {drama.currentEpisode} 集</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {episodes.map((ep) => {
            const aired = ep <= drama.currentEpisode;
            return (
              <button
                key={ep}
                type="button"
                onClick={() => showToast("视频资源暂未开放，敬请期待")}
                className={`flex items-center justify-between rounded-md border px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                  aired
                    ? "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    : "border-background-200 bg-background-100 text-foreground-400"
                }`}
              >
                <span className="whitespace-nowrap">第 {ep} 集</span>
                <i className={aired ? "ri-play-circle-line" : "ri-lock-line"}></i>
              </button>
            );
          })}
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-magic-line text-lg"></i>
            </span>
            相关推荐
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((d, i) => (
              <DramaCard key={d.id} drama={d} index={i} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-12 pb-4">
        <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className="ri-chat-3-line text-lg"></i>
          </span>
          用户评论
          <span className="text-sm font-normal text-foreground-400">（演示内容）</span>
        </h2>
        <div className="mt-5 space-y-4">
          {demoComments.map((c) => (
            <div key={c.name} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                {c.name.slice(0, 1)}
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground-900">{c.name}</span>
                  <span className="text-xs text-foreground-400">{c.date}</span>
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{c.content}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-50 p-4 text-center text-sm text-foreground-400">
          评论功能为演示样式，暂无真实提交入口
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-md bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}